import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import ManagerVerbsTable from './ManagerVerbsTable';
import VerbWorkCards from './VerbWorkCards';
import { WordStatus } from './meta';
import { UseVocabularyVerbsViewState } from './useVocabularyVerbsView';

export interface VocabularyVerbsViewProps {
  view: UseVocabularyVerbsViewState;
  /** Single-row status setter — wired to the BE endpoint that flips a verb's
   * lifecycle. Until BE exposes a unified `status` enum, callers translate
   * the 4-state value back into `isLearned` / `isActiveSpeech` mutations. */
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
  /** Optional Alert/error banner rendered above the content. */
  alert?: React.ReactNode;
  /** Pagination footer (defer to caller — page-level data lives there). */
  paginationFooter?: React.ReactNode;
}

/**
 * Thin orchestrator: picks Table vs Cards based on view.viewMode (force-cards
 * on phone since the table is unusable below 640px). All chrome — header,
 * filter bar, add panel, settings popover, undo toast — is composed by the
 * page that hosts this view; this component owns ONLY the verb-list rendering.
 *
 * Mirrors {@link import('components/vocabularyWordsView/VocabularyWordsView').default}
 * for words.
 */
const VocabularyVerbsView: React.FC<VocabularyVerbsViewProps> = ({
  view,
  onSetStatus,
  onDeleteOne,
  alert,
  paginationFooter,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const renderMode: 'cards' | 'table' = isPhone ? 'cards' : view.viewMode;

  return (
    <>
      {alert && <Box sx={{ mb: 2 }}>{alert}</Box>}

      {renderMode === 'table' ? (
        <ManagerVerbsTable
          view={view}
          onSetStatus={onSetStatus}
          onDeleteOne={onDeleteOne}
        />
      ) : (
        <VerbWorkCards
          view={view}
          onSetStatus={onSetStatus}
          onDeleteOne={onDeleteOne}
        />
      )}

      {paginationFooter && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            mt: 2.5,
            alignItems: 'center',
          }}
        >
          {paginationFooter}
        </Box>
      )}
    </>
  );
};

export default VocabularyVerbsView;
